"use client";

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type NameSort = 'asc' | 'desc';
export type ScheduleViewMode = 'day' | 'week' | '14day' | 'month';
export type GridListMode = 'grid' | 'list';

interface StoredPrefs {
  nameSort: NameSort;
  scheduleViewMode: ScheduleViewMode;
  capstoneViewMode: GridListMode;
}

interface UserPreferences extends StoredPrefs {
  setNameSort: (v: NameSort) => void;
  setScheduleViewMode: (v: ScheduleViewMode) => void;
  setCapstoneViewMode: (v: GridListMode) => void;
}

const STORAGE_KEY = 'vfe:prefs';

const DEFAULTS: StoredPrefs = {
  nameSort: 'asc',
  scheduleViewMode: 'week',
  capstoneViewMode: 'grid',
};

function loadPrefs(): StoredPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

function savePref<K extends keyof StoredPrefs>(key: K, value: StoredPrefs[K]) {
  try {
    const current = loadPrefs();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, [key]: value }));
  } catch { /* storage unavailable */ }
}

const UserPreferencesContext = createContext<UserPreferences | null>(null);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<StoredPrefs>(DEFAULTS);

  // Hydrate from localStorage after mount
  useEffect(() => {
    setPrefs(loadPrefs());
  }, []);

  const setNameSort = useCallback((v: NameSort) => {
    setPrefs(p => ({ ...p, nameSort: v }));
    savePref('nameSort', v);
  }, []);

  const setScheduleViewMode = useCallback((v: ScheduleViewMode) => {
    setPrefs(p => ({ ...p, scheduleViewMode: v }));
    savePref('scheduleViewMode', v);
  }, []);

  const setCapstoneViewMode = useCallback((v: GridListMode) => {
    setPrefs(p => ({ ...p, capstoneViewMode: v }));
    savePref('capstoneViewMode', v);
  }, []);

  return (
    <UserPreferencesContext.Provider value={{ ...prefs, setNameSort, setScheduleViewMode, setCapstoneViewMode }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferences {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  return ctx;
}
